import { useMemo } from "react";
import { routes } from "@/router";
import { buildCurrentProductDefinition } from "@/utils/buildCurrentDynamicFormDefinition";
import { buildDefaultValues, findAnswer, formatAnswers } from "@/utils/dynamicForm.util";
import { PredictiveProductAnswerDto } from "@advanced-sales-technologies/backend-client/models/PredictiveProductAnswerDto";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { FormValues } from "@/types/DynamicForm.type";
import { useGetProduct } from "@/hooks/api/predictive/product/useGetProduct";
import { useGetProductQuestions } from "@/hooks/api/predictive/product/useGetProductQuestions";
import { useUpdateProduct } from "@/hooks/api/predictive/product/useUpdateProduct";
import Spinner from "@/components/atoms/Spinner";
import DynamicForm from "@/components/molecules/form/DynamicForm";
import PredictiveLeadsHeader from "@/components/organisms/predictive/PredictiveLeadsHeader";
import { productFormDefinition, ProductFormValues } from "@/components/organisms/predictive/productFormDefinition";

const UpdateProduct = () => {
  const { t } = useTranslation();
  const { data: questions, isLoading: questionsLoading } = useGetProductQuestions();
  const { updateProduct, isPending } = useUpdateProduct();
  const { productId } = useParams();
  const navigate = useNavigate();
  const { data: product, isLoading: productIdLoading } = useGetProduct({ id: productId! });

  const currentProductDefinition = useMemo(
    () =>
      buildCurrentProductDefinition(productFormDefinition(t), questions, {
        includeName: true,
        includeHeaders: true,
        includeWarnings: true,
      }),
    [productFormDefinition, questions],
  );

  const defaultValues = useMemo<ProductFormValues>(() => {
    const out = buildDefaultValues(currentProductDefinition) as ProductFormValues;

    if (!product) return out;

    currentProductDefinition.forEach((field) => {
      if (!("name" in field)) return; // skip display-only

      if (field.name === "name") {
        out[field.name] = (product as Record<string, unknown>)[field.name] ?? out[field.name];
        return;
      }

      const answer = findAnswer(product.answers, field.target!);
      if (answer !== undefined) {
        const key = field.name as keyof ProductFormValues;
        out[key] = answer as ProductFormValues[typeof key];
      }
    });

    return out;
  }, [currentProductDefinition, product]);

  const handleSubmit = async (data: ProductFormValues) => {
    const { name, ...answers } = data;

    const formatted = formatAnswers(
      currentProductDefinition,
      answers as FormValues<typeof currentProductDefinition>,
    ) as PredictiveProductAnswerDto[];

    updateProduct(
      {
        id: product?.id as string,
        name: name as string,
        answers: formatted,
        displayToast: true,
      },
      {
        onSuccess: () => navigate(routes.predictive),
      },
    );
  };

  return (
    <div className={"h-screen w-full overflow-y-auto px-12"}>
      <PredictiveLeadsHeader showIntroButton />

      {questionsLoading || productIdLoading ? (
        <div className={"flex h-full items-center justify-center"}>
          <Spinner />
        </div>
      ) : (
        <div className={"mx-auto mt-12 max-w-xl"}>
          <DynamicForm
            config={currentProductDefinition}
            defaultValues={defaultValues}
            onSubmit={handleSubmit}
            isPending={isPending}
            submitBtnText={t("predictive.product.update")}
          />
        </div>
      )}
    </div>
  );
};

export default UpdateProduct;
